/**
 * resource-name — display-name resolution for the Clockify resource directory (RES-01).
 *
 * PROVENANCE: Clockify has NO separate display-name/full-name field. The workspace users endpoint
 * returns a single `name` (packages/resources/services/clockify.service.ts → `ClockifyUser.name`),
 * which Clockify defaults to the email local-part for anyone who never set a display name — see
 * docs/clockify_users_sample.json, where `"adnan.afzal"` and `"ahmad.mudassir"` are the literal
 * `name` values — and `null` for NOT_REGISTERED invitees. The sync then stores
 * `name = user.name?.trim() || user.email?.trim()` (packages/resources/handlers/sync-clockify.ts),
 * so `resources.name` can legitimately hold a raw account identifier rather than a person's name.
 *
 * `resources.name` IS editable (PATCH /api/resources/{id}) and is deliberately NOT overwritten by
 * re-syncs, so an admin-entered name always wins once set. Until then we must not render the raw
 * identifier as if it were a verified name: it is returned flagged so the caller can style it
 * distinctly (muted/italic) and keep the raw identifier reachable via tooltip / profile details.
 */

/**
 * A `name` shaped like an account handle: segments joined by `.` or `_` with no whitespace
 * (e.g. "adnan.afzal", "Adnan.Afzal"). Matched case-insensitively against a lowercased name.
 * `-` is deliberately NOT a separator here — hyphens are common in real surnames ("Mary-Jane").
 */
const IDENTIFIER_PATTERN = /^[a-z0-9]+(?:[._][a-z0-9]+)+$/;

/** Shown when the stored name is an account identifier rather than a person's name. */
export const IDENTIFIER_NAME_HINT =
  'No display name in Clockify — showing the account identifier. Edit the resource to set a display name.';

/** Shown when neither a name nor an identifier is available. */
export const MISSING_NAME_HINT =
  'No name available from Clockify. Edit the resource to set a display name.';

/** Label rendered when nothing usable is stored. */
export const MISSING_NAME_LABEL = 'Unknown';

export interface ResourceDisplayName {
  /** Text to render in the Name column. */
  label: string;
  /** True when `label` is NOT a human-entered name — render it distinctly, never as a plain name. */
  isFallback: boolean;
  /** Raw stored `name`, retained for tooltips / profile details even when it is an identifier. */
  identifier: string | null;
  /** Why the fallback was used. Only set when `isFallback` is true. */
  hint?: string;
}

/** True when `name` is an account identifier (email, email local-part, or dotted handle). */
function isIdentifierLike(name: string, email: string | null | undefined): boolean {
  // A whole email address stored in the name column (sync falls back to email when name is null).
  if (name.includes('@')) return true;

  // A dotted/underscored single token — Clockify's default for accounts with no display name.
  if (!/\s/.test(name) && IDENTIFIER_PATTERN.test(name.toLowerCase())) return true;

  // A separator-free token that is exactly the email local part AND still in its raw lowercase
  // form. This catches "cher" against cher@… while leaving a deliberately capitalised mononym
  // ("Cher") alone — capitalisation is the only signal that someone typed it on purpose.
  const local = email?.split('@')[0]?.trim() ?? '';
  return (
    local !== '' && name === name.toLowerCase() && name.toLowerCase() === local.toLowerCase()
  );
}

/**
 * Resolve what to render for a resource's name, preferring a real human-entered name and flagging
 * the identifier fallback so it is never presented as a verified name.
 */
export function resolveResourceName(resource: {
  name?: string | null;
  email?: string | null;
}): ResourceDisplayName {
  const name = resource.name?.trim() ?? '';

  if (!name) {
    return { label: MISSING_NAME_LABEL, isFallback: true, identifier: null, hint: MISSING_NAME_HINT };
  }

  if (isIdentifierLike(name, resource.email)) {
    return { label: name, isFallback: true, identifier: name, hint: IDENTIFIER_NAME_HINT };
  }

  return { label: name, isFallback: false, identifier: name };
}
